/* eslint-disable @typescript-eslint/naming-convention */
import { Control, ControlConditions, ControlType } from "@/types/controls";
import { spacingBarsPresetSlug } from "@/lib/elements/constants";

type CodeHelperParams = { control: Control; twigPath: string };

const wrapPathWithMustaches = ({ twigPath }: CodeHelperParams) => {
  return "{{ " + twigPath + " }}";
};

const toggleCode = ({ twigPath }: CodeHelperParams) => {
  return `{% if ${twigPath} %}
toggle is on
{% else %}
toggle is off
{% endif %}`;
};

const dropdownOrButtonBarCode = ({ control, twigPath }: CodeHelperParams) => {
  // TODO: use strict equality? Is this shit always a string? Couldnt it values be a number or some shit? Or is it a string by the time it gets to Twig?
  // or since we dont know, dont use strict equality? lol this sounds like it could waste an unlucky element developer's time
  return control.options.items
    ? control.options.items
        .map((item, index) => {
          const ifOrElseIf = index ? "elseif" : "if";
          return (
            `{% ${ifOrElseIf} ${twigPath} == '${item.value}' %}` +
            `\n  CSS or HTML here` +
            `\n`
          );
        })
        .join("") + "\n{% endif %}"
    : "no items yo";
};

const multiselectCode = ({ control, twigPath }: CodeHelperParams) => {
  return control.options.items
    ? control.options.items
        .map((item, index) => {
          const ifOrElseIf = index ? "elseif" : "if";
          return (
            `{% ${ifOrElseIf} '${item.value}' in ${twigPath} %}` +
            `\n  CSS or HTML here` +
            `\n`
          );
        })
        .join("") + "\n{% endif %}"
    : "no items yo";
};

const shadowCode = ({ control, twigPath }: CodeHelperParams) => {
  const isText = control?.options?.shadowOptions?.type === "text";
  const property = isText ? "text-shadow" : "box-shadow";

  return `${property}: {{ ${twigPath}.style }};`;
};

const iconCode = ({ twigPath }: CodeHelperParams) => `
<style>
/* put this in your default CSS */
.some-class-to-wrap-the-icon-svg { /* prefix with element classname, i.e. .element-classname .some-class-to-wrap-the-icon */
    display: flex;
    font-size: 24px;
}

.some-class-to-wrap-the-icon-svg > svg { /* also, prefix with element classname */
    font-size: inherit;
    width: 1em;
    height: 1em;
    stroke-width: 0;
    stroke: currentColor;
    fill: currentColor;
}

/* put this in your twig CSS */
%%SELECTOR%% .some-class-to-wrap-the-icon-svg {
    color: #ff0000;
    font-size: 30px;
}
</style>
<div class="some-class-to-wrap-the-icon-svg">
{{ ${twigPath}.svgCode }}
</div>
`;

const repeaterCode = ({ twigPath }: CodeHelperParams) => {
  return `
    {% for item in ${twigPath} %}
        <div>
            repeated
        </div>
    {% endfor %}
    `;
};

const focusPointCode = ({ twigPath }: CodeHelperParams) => {
  return `
  /* could be used for transforms */
  transform-origin: {{ ${twigPath}.x }}% {{ ${twigPath}.y }}%;

  /* or positioning a background */
  background-position: {{ ${twigPath}.x }}% {{ ${twigPath}.y }}%;
  `;
};

const presetsWithMacroOfTheSameNameAndBasicCodeHelp = [
  "layout_basic_flex",
  "layout",
  "borders",
  "background",
  "css",
  "size",
  "grid",
  ...spacingBarsPresetSlug
];

const presetSectionCode = ({ control, twigPath }: CodeHelperParams) => {
  const presetType = control.options.sectionOptions?.preset?.slug?.replace(
    "EssentialElements\\",
    ""
  );

  if (!presetType) return "undefined preset type";

  if (presetsWithMacroOfTheSameNameAndBasicCodeHelp.includes(presetType)) {
    return `{{ macros.${presetType}(${twigPath}) }}`;
  }

  if (presetType === "effects") {
    return `
    {{ macros.effects(${twigPath}, '%%SELECTOR%%') }}
    {{ macros.effectsHover(${twigPath}, '%%SELECTOR%%:hover') }}
    `;
  }

  if (presetType === "effects_no_hover") {
    return `{{ macros.effects(${twigPath}, '%%SELECTOR%%') }}`;
  }

  if (presetType === "filter") {
    return `{{ macros.filters(${twigPath}) }}`;
  }

  if (
    presetType === "typography" ||
    presetType === "typography_with_nothing" ||
    presetType === "typography_with_align" ||
    presetType === "typography_with_effects" ||
    presetType === "typography_with_effects_and_align"
  ) {
    return `{{ macros.typography(${twigPath}, globalSettings) }}`;
  }

  if (presetType === "simpleLayout") {
    return `{{ macros.simpleLayout(${twigPath}, breakpoint) }}`;
  }

  if (presetType === "fancy_background") {
    return `
      {{ macros.fancyBackgroundHtml(${twigPath}) }}
      {{ macros.fancyBackgroundCss('%%SELECTOR%%', ${twigPath}, breakpoint) }}
    `;
  }

  if (
    presetType === "typography_with_hoverable_color" ||
    presetType === "typography_with_align_with_hoverable_color" ||
    presetType === "typography_with_effects_with_hoverable_color_and_effects" ||
    presetType ===
      "typography_with_effects_and_align_with_hoverable_color_and_effects"
  ) {
    return `
    %%SELECTOR%% {
      {{ macros.typography(${twigPath}, globalSettings) }}
    }

    %%SELECTOR%%:hover {
      {{ macros.typographyWithHoverableColorAndEffects(${twigPath}) }}
    }
    `;
  }

  if (
    presetType === "typography_with_hoverable_everything" ||
    presetType === "typography_with_align_with_hoverable_everything" ||
    presetType === "typography_with_effects_with_hoverable_everything" ||
    presetType === "typography_with_effects_and_align_with_hoverable_everything"
  ) {
    return `
    %%SELECTOR%% {
      {{ macros.typography(${twigPath}, globalSettings) }}
    }

    %%SELECTOR%%:hover {
      {{ macros.typographyWithHoverableEverything(${twigPath}) }}
    }
    `;
  }

  if (presetType === "AtomV1ButtonContent") {
    return `
    <!-- In order to set default styles for your buttons, use the following macros: -->
    <!-- You can overwrite these settings using design options  -->

    <!-- Button (Primary) -->
    {{ macros.atomV1ButtonHtml(${twigPath}, 'my-button-custom-class', path.to.button_design_preset_section, '${twigPath}.text') }}

    <!-- Button (Secondary) -->
    {{ macros.atomV1ButtonHtml(${twigPath}, 'my-button-custom-class', path.to.button_design_preset_section, 'secondary', '${twigPath}.text') }}

    <!-- Button (Custom) -->
    {{ macros.atomV1ButtonHtml(${twigPath}, 'my-button-custom-class', path.to.button_design_preset_section, 'custom', '${twigPath}.text') }}

    <!-- Button (Text) -->
    {{ macros.atomV1ButtonHtml(${twigPath}, 'my-button-custom-class', path.to.button_design_preset_section, 'text', '${twigPath}.text') }}

    <!-- Form Button (Submit) -->
    {{ macros.atomV1ButtonHtmlManual('Submit', false, '%%SELECTOR%% .my-button-custom-class', path.to.button_design_preset_section, 'primary/secondary/custom/text', 'custom-button-id', true, '${twigPath}.text') }}

    <!-- SSR Button -->
    echo /Breakdance/Elements/AtomV1Button/render($propertiesData['content']['button'], 'my-button-custom-class', $propertiesData['design']['button'], 'primary/secondary/custom/text');

    <!-- SSR Form Button -->
    echo /Breakdance/Elements/AtomV1Button/renderFormButton($propertiesData['content']['submit_button']['text'], 'my-button-custom-class', $propertiesData['design']['form_button'], 'primary/secondary/custom/text', 'custom-button-id');


`;
  }

  if (presetType === "AtomV1ButtonDesign") {
    return `
      {{ macros.atomV1ButtonCss(${twigPath}, 'my-button-custom-class', '%%SELECTOR%%', breakpoint, globalSettings) }}
    `;
  }

  if (presetType === "AtomV1CustomButtonDesign") {
    return `

      {{ macros.atomV1ButtonButton(${twigPath}, '%%SELECTOR%% .my-button-custom-class', 'primary/secondary/custom', breakpoint, globalSettings) }}


    `;
  }

  if (presetType === "AtomV1IconDesignWithHover") {
    return `
    HTML:
    {{ macros.atomV1IconHtml('my-icon-class', path.to.icon, path.to.rotate (number between 1 and 360) or false, path.to.link (link control) or false, ${twigPath}) }}

    CSS:
    {{ macros.atomV1IconCss(${twigPath}, 'my-icon-class', '%%SELECTOR%%', true) }}`;
  }

  if (presetType === "AtomV1IconDesign") {
    return `
    HTML:
    {{ macros.atomV1IconHtml('my-icon-class', path.to.icon, path.to.rotate (number between 1 and 360) or false, path.to.link (link control) or false, ${twigPath}) }}

    CSS:
    {{ macros.atomV1IconCss(${twigPath}, 'my-icon-class', '%%SELECTOR%%') }}`;
  }

  if (presetType === "AtomV1FormDesign") {
    return `{{ macros.atomV1FormCss(${twigPath}, '%%SELECTOR%%') }}`;
  }

  if (presetType === "tabs_design") {
    return `{{ macros.atomV1TabsCss('%%SELECTOR%%', ${twigPath}, breakpoint, globalSettings) }}`;
  }

  if (presetType === "WooGlobalStylerOverride") {
    return `{{ macros.wooGlobalStyler(${twigPath}, '%%SELECTOR%%', breakpoint, globalSettings) }}`;
  }

  if (presetType === "pagination") {
    return `{{ macros.pagination(${twigPath}, '%%SELECTOR%%', listSelector, activeSelector, dotsSelector) }}`;
  }

  if (
    presetType === "wooPresetInputsDesign" ||
    presetType === "wooPresetInputsDesignNoSelect2"
  ) {
    return `
    %%SELECTOR%% {
      {{ macros.wooPresetInputsDesign(${twigPath}) }}
    }`;
  }

  if (presetType === "wooProductsListElements") {
    return `
    {{ macros.wooPresetProductsListElementsDesign('%%SELECTOR%%', ${twigPath}) }}
    `;
  }

  if (presetType === "wooProductsListLayout") {
    return `
    {{ macros.wooPresetProductsListLayoutDesign('%%SELECTOR%%', ${twigPath}) }}
    `;
  }

  if (presetType === "wooProductWrapperDesign") {
    return `
    {{ macros.wooPresetProductWrapperDesign('%%SELECTOR%%', ${twigPath}) }}
    `;
  }

  if (["lightbox_design", "lightbox_single_design"].includes(presetType)) {
    return `{{ macros.atomV1LightboxCss(${twigPath}) }}`;
  }

  if (presetType === "AtomV1SwiperSettings") {
    return `
HTML:

<div class="breakdance-swiper-wrapper" data-swiper-id="%%ID%%">
  <div class="swiper">
    <div class="swiper-wrapper">
      %%CHILDREN%%
    </div>
  </div>
  
  {{ macros.AtomV1SwiperPaginationAndArrowsHtml(design.slider) }}
</div>

----

CSS:
{{ macros.AtomV1SwiperCss(${twigPath}, '%%SELECTOR%%', globalSettings, isBaseBreakpoint) }}
`;
  }

  if (presetType === "borders_without_shadows") {
    return `
    {{ macros.borders_without_shadows(${twigPath}) }}
    `;
  }

  return `no code help for preset section type of ${presetType}`;
};

export const codeForControls: {
  [key in ControlType]: undefined | ((args: CodeHelperParams) => string);
} = {
  trigger_action_button: undefined,
  border_complex: ({ twigPath }) => `{{ macros.border_complex(${twigPath}) }}`,
  border_radius: ({ twigPath }) => `{{ macros.border_radius(${twigPath}) }}`,
  breakpoint_dropdown: ({ twigPath }) =>
    `
    {% if breakpoint == ${twigPath} %}
    %%SELECTOR%% {
        /** some css that will apply to the selected breakpoint */
    }
    {% endif %}
    `,
  button_bar: dropdownOrButtonBarCode,
  dropdown: dropdownOrButtonBarCode,
  code: wrapPathWithMustaches,
  color: ({ control, twigPath }) => {
    if (control.options.colorOptions?.type === "solidAndGradient") {
      return `
        /* Background Color */
        {{ macros.backgroundColor(${twigPath}) }}
        
        /* Or */
        
        /* Text Color */
        {{ macros.textColor(${twigPath}) }}
        
        /* Or */
        
        /* Border Color */
        {{ macros.borderColor(${twigPath}) }}
      `;
    }

    return `background-color: {{ ${twigPath} }}`;
  },
  gradient: ({ twigPath }) => `background-image: {{ ${twigPath}.style }}`,
  shadow: shadowCode,
  icon: iconCode,
  number: wrapPathWithMustaches,
  repeater: repeaterCode,
  inline_repeater: repeaterCode,
  richtext: wrapPathWithMustaches,
  toggle: toggleCode,
  spacing_complex: ({ twigPath }) =>
    `{{ macros.spacing_complex(${twigPath}, 'margin') }}`,
  text: wrapPathWithMustaches,
  url: wrapPathWithMustaches,
  unit: ({ twigPath }) => `font-size: {{ ${twigPath}.style }};`,
  wpmedia: ({ control, twigPath }) => {
    if (control.options?.mediaOptions?.multiple) {
      return `
      {% for item in ${twigPath} %}
        {{ macros.getMedia(item, content.content.size, content.content.lazyLoad) }}
      {% endfor %}
      `;
    }

    return `
      This macro might return an img, video, or iframe tag, depending on the media type.
      {{ macros.getMedia(${twigPath}, content.content.size, content.content.lazyLoad) }}
      
      Or you can render only images
      {{ macros.image(${twigPath}, content.content.size, alt, lazyLoad) }}
    `;
  },
  video: ({ twigPath }) => {
    return `
      {{ macros.video(${twigPath}) }}
    `;
  },
  focus_point: focusPointCode,
  section: presetSectionCode,
  multiselect: multiselectCode,
  condition: undefined,
  font_family: undefined,
  class: undefined,
  global_colors: undefined,
  override_variables: undefined,
  global_block_chooser: undefined,
  component_chooser: undefined,
  typography: undefined,
  custom_typography: undefined,
  typography_preset: undefined,
  message: undefined,
  post_chooser: undefined,
  element_screenshot: undefined,
  slider: undefined,
  add_registered_children: undefined,
  builder_behavior: undefined,
  alert_box: undefined,
  pro_only_alert_box: undefined,
  date_picker: ({ twigPath }) => {
    return `{{ ${twigPath} }}`;
  },
  time_picker: ({ twigPath }) => {
    return `{{ ${twigPath} }}`;
  },
  api_key_input: undefined,
  link: ({ twigPath }) => {
    return `
     {{ macros.linkStart(${twigPath}) }}
         Link Text
     {{ macros.linkEnd() }}
    `;
  },
  media_size_dropdown: ({ twigPath }) => {
    return `{{ ${twigPath} }}`;
  },
  wp_query: () =>
    `Use this control in your SSR.php file. For example:
    \\Breakdance\\WpQueryControl\\getWpQueryArgumentsFromWpQueryControlProperties($propertiesData['content']['wp_query']['query']);`,
  conditional_form_field: undefined,
  interactions: undefined
};

export const wrapWithCondition = (
  controlConditions: ControlConditions,
  code: string
) => {
  let ifCondition = "";

  if (Array.isArray(controlConditions)) {
    ifCondition = controlConditions.reduce((conditionsAcc, conditions) => {
      const andCondition = conditions.reduce((acc, { path, value }) => {
        if (acc) {
          return `${acc} and ${path} == "${value}"`;
        }

        return `${path} == "${value}"`;
      }, "");

      if (conditionsAcc) {
        return `${conditionsAcc} or (${andCondition})`;
      }

      return `(${andCondition})`;
    }, "");
  } else {
    const { path, value } = controlConditions; // TODO: what about operand
    // TODO - this is garbage for controls conditionally displayed by a toggle
    ifCondition = `${path} == "${value}"`;
  }

  return `
      {% if ${ifCondition} %}
      ${code}
      TODO - examples of not equals, is one of, is set, is not set
      {% elseif %}
      ...
      {% else %}
      ...
      {% endif %}
      `;
};
